/** KA Start→Done timing — shared helpers so poll/load never wipes a confirmed Start. */

export function isProductionStageStarted(info, role = "") {
  if (!info) return false;
  const rk = (role || info.role || "").trim().toLowerCase();
  return Boolean(
    info.is_stage_started ||
      info.stage_started_at ||
      (rk && info[`production_${rk}_started_at`])
  );
}

/** Keep Start state when a stale poll omits timing fields (SELECT drift / race). */
export function mergeProductionTaskInfo(prev, next, role = "") {
  if (!next) return prev;
  if (!prev) return next;
  const rk = (role || next.role || prev.role || "").trim().toLowerCase();
  const startedKey = rk ? `production_${rk}_started_at` : "";
  const workerKey = rk ? `production_${rk}_worker` : "";
  const prevStarted = isProductionStageStarted(prev, rk);
  const nextStarted = isProductionStageStarted(next, rk);
  if (prevStarted && !nextStarted) {
    return {
      ...next,
      is_stage_started: true,
      stage_started_at: prev.stage_started_at || prev[startedKey] || next.stage_started_at,
      stage_worker: prev.stage_worker || prev[workerKey] || next.stage_worker,
      ...(startedKey && (prev[startedKey] || prev.stage_started_at)
        ? { [startedKey]: prev[startedKey] || prev.stage_started_at }
        : {}),
      ...(workerKey && (prev[workerKey] || prev.stage_worker)
        ? { [workerKey]: prev[workerKey] || prev.stage_worker }
        : {}),
    };
  }
  const prevDone = Boolean(prev.is_done);
  const nextDone = Boolean(next.is_done);
  if (prevDone && !nextDone) {
    const doneKey = rk ? `production_${rk}_done_at` : "";
    return {
      ...next,
      is_done: true,
      done_at: prev.done_at || prev[doneKey] || next.done_at,
      ...(doneKey && (prev[doneKey] || prev.done_at)
        ? { [doneKey]: prev[doneKey] || prev.done_at }
        : {}),
    };
  }
  return next;
}

/** RTG fridge stock or Pastry — decorator only (skip baker + froster). */
export function isRtgDecorSkip(info) {
  return Boolean(
    info?.is_rtg_decor_only ||
      info?.rtg_decor_skip_baker_froster ||
      info?.rtg_wrong_role ||
      info?.is_pastry ||
      info?.is_pastry_decor_only ||
      info?.ka_pastry_decor_only ||
      info?.pastry_decor_skip_baker_froster ||
      info?.pastry_wrong_role
  );
}

/** Decorator may proceed (Start / calls / photos) without waiting on froster when RTG decor-only. */
export function isDecoratorUpstreamReady(info) {
  if (!info) return false;
  if (isRtgDecorSkip(info)) return true;
  if (info.is_dry_cake || info.dry_cake_skip_froster) {
    return Boolean(info.production_baker_done_at);
  }
  return Boolean(info.production_froster_done_at);
}

export async function postProductionStageStart({
  token,
  hubOrderId,
  role,
  isHubMode,
  productionAuthBody,
  parseJson,
}) {
  const res = await fetch("/api/production/task/start", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(productionAuthBody(token, hubOrderId, role, isHubMode)),
  });
  const data = await parseJson(res);
  const order = data.order || data;
  if (!isProductionStageStarted(order, role)) {
    throw new Error("Start did not save — pull to refresh and try again.");
  }
  return order;
}
